"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { LoaderCircle, X } from "lucide-react";

import { getAnalyticsSessionId } from "@/lib/analytics";
import { createPublicLead } from "@/lib/leads";
import type { PublicStoreCampaign } from "@/types/public-campaign";

type Props = {
  campaign: PublicStoreCampaign;
  companySlug: string;
  onClose: () => void;
};

export default function CampaignInterestModal({
  campaign,
  companySlug,
  onClose,
}: Props) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href]'
        )
      );

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [onClose]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (saving || success) return;

    const cleanName = name.trim();
    const cleanPhone = phone.trim();
    const phoneDigits = Array.from(cleanPhone)
      .filter((character) => character >= "0" && character <= "9")
      .join("");

    if (cleanName.length < 2 || phoneDigits.length < 10) {
      setError("Informe seu nome e um telefone válido.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await createPublicLead(companySlug, {
        name: cleanName,
        phone: cleanPhone,
        email: email.trim() || undefined,
        message: message.trim() || undefined,
        source: "store",
        lead_type: "general",
        session_id: getAnalyticsSessionId(),
      });

      setSuccess(true);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Não foi possível enviar seu interesse. Tente novamente."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto bg-slate-950/70 p-3 sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-interest-title"
        className="my-auto w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-7"
        style={{ maxHeight: "calc(100dvh - 24px)" }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-blue-600">
              Campanha publicitária
            </p>
            <h2
              id="campaign-interest-title"
              className="mt-2 text-xl font-bold text-slate-950"
            >
              {success ? "Interesse enviado" : campaign.title}
            </h2>
          </div>

          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fechar formulário de interesse"
            className="rounded-xl p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className="mt-5" role="status">
            <p className="text-sm leading-6 text-slate-700">
              Seu interesse foi enviado. A empresa poderá entrar em contato
              pelos canais informados.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white"
            >
              Concluir
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            {campaign.description ? (
              <p className="whitespace-pre-line text-sm leading-6 text-slate-600">
                {campaign.description}
              </p>
            ) : null}

            <label className="block text-sm font-semibold text-slate-800">
              Nome *
              <input
                required
                autoFocus
                maxLength={150}
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                className="mt-1 block w-full rounded-xl border border-slate-300 px-3 py-3 font-normal"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-800">
              WhatsApp ou telefone *
              <input
                required
                type="tel"
                maxLength={20}
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                autoComplete="tel"
                className="mt-1 block w-full rounded-xl border border-slate-300 px-3 py-3 font-normal"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-800">
              E-mail (opcional)
              <input
                type="email"
                maxLength={254}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                className="mt-1 block w-full rounded-xl border border-slate-300 px-3 py-3 font-normal"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-800">
              Mensagem (opcional)
              <textarea
                rows={3}
                maxLength={2000}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                className="mt-1 block w-full resize-y rounded-xl border border-slate-300 px-3 py-3 font-normal"
              />
            </label>

            {error ? (
              <p role="alert" className="text-sm font-medium text-red-700">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={saving}
              className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white disabled:opacity-60"
            >
              {saving ? (
                <LoaderCircle size={17} className="animate-spin" />
              ) : null}
              {saving ? "Enviando..." : "Enviar interesse"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
